import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  SG_ADD_PUBLIC_UPLOAD,
  SG_FETCH_PUBLIC_UPLOAD,
  SG_EDIT_PUBLIC_UPLOAD,
  SG_GET_ORGANIZATION_SELECT,
} from "constants/actions";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Alert, AlertDescription } from "../components/ui/alert";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import { Upload, Save, X, CheckCircle2, FileText, ExternalLink, Loader2, Key, RefreshCw } from "lucide-react";

const CATEGORY_OPTIONS = [
  { value: "pdf_template", label: "PDF Template" },
  { value: "logo", label: "Logo" },
  { value: "css", label: "CSS" },
  { value: "js", label: "JavaScript" },
  { value: "json", label: "JSON" },
  { value: "branding", label: "Branding" },
  { value: "document", label: "Document" },
  { value: "image", label: "Image" },
  { value: "other", label: "Other" },
];

const STATUS_OPTIONS = [
  { value: "1", label: "Active" },
  { value: "0", label: "Inactive" },
];

export default function UploadForm() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const { publicUploadDetail, loading } = useSelector(
    (state) => state.public_upload
  );
  const { organization_select } = useSelector(
    (state) => state.organizations || {}
  );

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    category: "",
    organization: "all",
    sort_order: 0,
    status: "1",
    metadata: {},
    is_public: "1",
    access_key: "",
  });

  const [file, setFile] = useState(null);
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    dispatch({ type: SG_GET_ORGANIZATION_SELECT });
    if (isEdit) {
      dispatch({ type: SG_FETCH_PUBLIC_UPLOAD, payload: { id } });
    }
  }, [id, isEdit]);

  useEffect(() => {
    if (isEdit && publicUploadDetail) {
      setFormData({
        name: publicUploadDetail.name || "",
        description: publicUploadDetail.description || "",
        category: publicUploadDetail.category || "",
        organization: publicUploadDetail.organization || "",
        sort_order: publicUploadDetail.sort_order || 0,
        status: String(publicUploadDetail.status || 1),
        metadata: publicUploadDetail.metadata || {},
        is_public: String((publicUploadDetail.is_public ? 1 : 0)),
        access_key: publicUploadDetail.access_key || "",
      });
    }
  }, [publicUploadDetail, isEdit]);

  const handleChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
    if (errors[name]) {
      setErrors({ ...errors, [name]: null });
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      if (!formData.name) {
        setFormData({ ...formData, name: selectedFile.name });
      }
      if (errors.file) {
        setErrors({ ...errors, file: null });
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!isEdit && !file) {
      newErrors.file = "Please select a file to upload";
    }

    if (!formData.category) {
      newErrors.category = "Category is required";
    }

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) {
      return;
    }

    if (isEdit) {
      // For edit, send JSON data (metadata only)
      const updateData = {
        id: id,
        name: formData.name,
        description: formData.description,
        status: parseInt(formData.status),
        sort_order: parseInt(formData.sort_order) || 0,
        metadata: formData.metadata,
        is_public: parseInt(formData.is_public),
        access_key: formData.access_key || undefined,
      };

      dispatch({ type: SG_EDIT_PUBLIC_UPLOAD, payload: updateData });
      setSuccess(true);
      setTimeout(() => {
        navigate("/app/public-uploads");
      }, 1500);
    } else {
      // For create, send FormData
      const formDataToSend = new FormData();
      formDataToSend.append("file", file);
      formDataToSend.append("name", formData.name);
      formDataToSend.append("category", formData.category);
      formDataToSend.append("is_public", formData.is_public);

      if (formData.description) {
        formDataToSend.append("description", formData.description);
      }

      if (formData.organization && formData.organization !== "all") {
        formDataToSend.append("organization", formData.organization);
      }

      if (formData.sort_order) {
        formDataToSend.append("sort_order", formData.sort_order);
      }

      if (formData.metadata && Object.keys(formData.metadata).length > 0) {
        formDataToSend.append("metadata", JSON.stringify(formData.metadata));
      }

      if (formData.access_key) {
        formDataToSend.append("access_key", formData.access_key);
      }

      dispatch({ type: SG_ADD_PUBLIC_UPLOAD, payload: formDataToSend });
      setSuccess(true);
      setTimeout(() => {
        navigate("/app/public-uploads");
      }, 1500);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl flex items-center gap-2">
            {isEdit ? <Save className="h-6 w-6" /> : <Upload className="h-6 w-6" />}
            {isEdit ? "Edit Upload Metadata" : "Upload New File"}
          </CardTitle>
          <CardDescription>
            {isEdit
              ? "Update file metadata (file cannot be changed)"
              : "Upload a new file to the system"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {success && (
            <Alert className="mb-6 border-green-500 bg-green-50">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-600">
                {isEdit ? "Upload updated successfully!" : "File uploaded successfully!"}
              </AlertDescription>
            </Alert>
          )}

          {isEdit && publicUploadDetail && (
            <Alert className="mb-6">
              <FileText className="h-4 w-4" />
              <AlertDescription>
                <div className="font-semibold mb-2">Current File Information</div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">File Name:</span>
                    <p className="font-medium">{publicUploadDetail.name}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">File Type:</span>
                    <p>
                      <Badge variant="outline">
                        {publicUploadDetail.file_type || publicUploadDetail.mime_type}
                      </Badge>
                    </p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">File Size:</span>
                    <p className="font-medium">{formatFileSize(publicUploadDetail.file_size)}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">File URL:</span>
                    <p>
                      <a
                        href={publicUploadDetail.file_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline inline-flex items-center gap-1"
                      >
                        Download <ExternalLink className="h-3 w-3" />
                      </a>
                    </p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Category:</span>
                    <p>
                      <Badge>{publicUploadDetail.category_text}</Badge>
                    </p>
                  </div>
                </div>
              </AlertDescription>
            </Alert>
          )}

          {loading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              <p className="mt-4 text-sm text-muted-foreground">Loading...</p>
            </div>
          ) : (
            <div className="space-y-6">
              {!isEdit && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="file">
                      Select File <span className="text-destructive">*</span>
                    </Label>
                    <div className="flex flex-col gap-2">
                      <Input
                        id="file"
                        type="file"
                        onChange={handleFileChange}
                        className={errors.file ? "border-destructive" : ""}
                      />
                      {errors.file && (
                        <p className="text-sm text-destructive">{errors.file}</p>
                      )}
                      {file && (
                        <Alert>
                          <FileText className="h-4 w-4" />
                          <AlertDescription>
                            <span className="font-semibold">{file.name}</span> - {formatFileSize(file.size)}
                          </AlertDescription>
                        </Alert>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="category">
                      Category <span className="text-destructive">*</span>
                    </Label>
                    <Select value={formData.category} onValueChange={(value) => handleChange("category", value)}>
                      <SelectTrigger id="category" className={errors.category ? "border-destructive" : ""}>
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        {CATEGORY_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.category && (
                      <p className="text-sm text-destructive">{errors.category}</p>
                    )}
                  </div>
                </>
              )}

              <div className="space-y-2">
                <Label htmlFor="name">
                  Name <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="name"
                  placeholder="File display name"
                  value={formData.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                  className={errors.name ? "border-destructive" : ""}
                />
                {errors.name && (
                  <p className="text-sm text-destructive">{errors.name}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  placeholder="File description (optional)"
                  value={formData.description}
                  onChange={(e) => handleChange("description", e.target.value)}
                  rows={4}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="is_public">Public Access</Label>
                  <Select
                    value={formData.is_public}
                    onValueChange={(value) => handleChange("is_public", value)}
                  >
                    <SelectTrigger id="is_public">
                      <SelectValue placeholder="Select access" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">Public</SelectItem>
                      <SelectItem value="0">Private (requires access key)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {formData.is_public === "0" && (
                  <div className="space-y-2">
                    <Label htmlFor="access_key" className="flex items-center gap-2">
                      <Key className="h-4 w-4" /> Access Key
                    </Label>
                    <div className="flex gap-2">
                      <Input
                        id="access_key"
                        placeholder="Enter or generate an access key"
                        value={formData.access_key}
                        onChange={(e) => handleChange("access_key", e.target.value)}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          const randomKey = Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2);
                          handleChange("access_key", randomKey);
                        }}
                        title="Generate key"
                      >
                        <RefreshCw className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {!isEdit && (
                <div className="space-y-2">
                  <Label htmlFor="organization">Organization (Optional)</Label>
                  <Select value={formData.organization} onValueChange={(value) => handleChange("organization", value)}>
                    <SelectTrigger id="organization">
                      <SelectValue placeholder="Select organization or leave empty for global" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Global (No organization)</SelectItem>
                      {(organization_select || []).map((org) => (
                        <SelectItem key={org.id} value={String(org.id)}>
                          {org.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="sort_order">Sort Order</Label>
                  <Input
                    id="sort_order"
                    type="number"
                    placeholder="0"
                    value={formData.sort_order}
                    onChange={(e) => handleChange("sort_order", e.target.value)}
                  />
                </div>

                {isEdit && (
                  <div className="space-y-2">
                    <Label htmlFor="status">Status</Label>
                    <Select value={formData.status} onValueChange={(value) => handleChange("status", value)}>
                      <SelectTrigger id="status">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUS_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              <Separator />

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => navigate("/app/public-uploads")}
                  disabled={loading}
                >
                  <X className="h-4 w-4 mr-2" />
                  Cancel
                </Button>
                <Button onClick={handleSubmit} disabled={loading}>
                  {isEdit ? (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Update Metadata
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4 mr-2" />
                      Upload File
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
